import React, { useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import {
    AlertTriangle,
    Ban,
    Building2,
    CalendarClock,
    CircleDollarSign,
    ExternalLink,
    FileText,
    Loader2,
    Plus,
    Receipt,
    User,
    Wallet,
} from "lucide-react";

import { PageHeader } from "@/components/common/PageHeader";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from "@/components/ui/table";
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { financeService } from "@/services/finance.service";
import { formatBRL, formatDate, formatDateTime, cn } from "@/lib/utils";
import {
    CATEGORY_LABELS,
    FINANCE_STATUS_LABELS,
    FinanceStatusBadge,
    PAYMENT_METHOD_LABELS,
    daysBetween,
} from "@/features/finance/components/FinanceStatusBadge";
import { RegisterPaymentDialog } from "@/features/finance/dialogs/RegisterPaymentDialog";

export default function AccountDetailsPage() {
    const { id } = useParams();
    const navigate = useNavigate();
    const qc = useQueryClient();

    const account = useQuery({
        queryKey: ["finance-account", id],
        queryFn: () => financeService.getAccount(id),
        enabled: !!id,
    });

    const [payingInst, setPayingInst] = useState(null);
    const [cancelOpen, setCancelOpen] = useState(false);

    const payMut = useMutation({
        mutationFn: (payload) => financeService.registerPayment(id, payload),
        onSuccess: (doc) => {
            toast.success("Pagamento registrado.");
            qc.invalidateQueries({ queryKey: ["finance-account", id] });
            qc.invalidateQueries({ queryKey: ["finance-accounts"] });
            qc.invalidateQueries({ queryKey: ["finance-dashboard"] });
            setPayingInst(null);
        },
        onError: (e) => toast.error(e?.response?.data?.detail || e?.message || "Falha ao registrar pagamento."),
    });

    const cancelMut = useMutation({
        mutationFn: () => financeService.cancelAccount(id, { reason: "Cancelada pelo usuário" }),
        onSuccess: () => {
            toast.success("Conta cancelada.");
            qc.invalidateQueries({ queryKey: ["finance-account", id] });
            qc.invalidateQueries({ queryKey: ["finance-accounts"] });
            qc.invalidateQueries({ queryKey: ["finance-dashboard"] });
            setCancelOpen(false);
        },
        onError: (e) => toast.error(e?.response?.data?.detail || e?.message || "Falha ao cancelar."),
    });

    if (account.isLoading) {
        return (
            <div className="grid place-items-center p-12 text-muted-foreground">
                <Loader2 className="h-6 w-6 animate-spin" />
            </div>
        );
    }
    if (!account.data) {
        return (
            <div className="p-6 text-sm text-muted-foreground">
                Conta não encontrada. <Button variant="link" onClick={() => navigate(-1)}>Voltar</Button>
            </div>
        );
    }
    const a = account.data;
    const isReceivable = a.type === "receivable";
    const listRoute = isReceivable ? "/finance/receivables" : "/finance/payables";
    const canCancel = a.status !== "cancelada" && Number(a.paid_amount || 0) === 0;

    const today = new Date();
    return (
        <div className="space-y-4" data-testid="finance-account-details">
            <PageHeader
                title={`${a.number} — ${a.description || "Conta"}`}
                description={
                    <>
                        {isReceivable ? "Conta a Receber" : "Conta a Pagar"}
                        {" · "}
                        <span>{CATEGORY_LABELS[a.category] || a.category}</span>
                    </>
                }
                backTo={listRoute}
                actions={
                    canCancel && (
                        <Button
                            variant="outline"
                            onClick={() => setCancelOpen(true)}
                            data-testid="finance-cancel-account-button"
                        >
                            <Ban className="mr-2 h-4 w-4" />
                            Cancelar conta
                        </Button>
                    )
                }
            />

            <div className="grid grid-cols-1 gap-4 xl:grid-cols-3">
                <Card className="xl:col-span-2" data-testid="finance-account-installments">
                    <CardHeader>
                        <CardTitle className="flex items-center gap-2">
                            <Wallet className="h-4 w-4" />
                            Parcelas
                        </CardTitle>
                    </CardHeader>
                    <CardContent>
                        <Table>
                            <TableHeader>
                                <TableRow>
                                    <TableHead className="w-[70px]">#</TableHead>
                                    <TableHead>Vencimento</TableHead>
                                    <TableHead className="text-right">Valor</TableHead>
                                    <TableHead className="text-right">Pago</TableHead>
                                    <TableHead className="text-right">Saldo</TableHead>
                                    <TableHead>Status</TableHead>
                                    <TableHead className="w-[180px] text-right">Ações</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {(a.installments || []).map((inst) => {
                                    const balance = Number(inst.amount || 0) - Number(inst.paid_amount || 0);
                                    const overdue = inst.status === "vencida";
                                    const dueDate = inst.due_date ? new Date(inst.due_date) : null;
                                    const daysLate = overdue && dueDate ? daysBetween(dueDate, today) : null;
                                    const canPay = inst.status !== "paga" && inst.status !== "cancelada" && a.status !== "cancelada";
                                    return (
                                        <TableRow
                                            key={inst.id}
                                            className={cn(overdue && "bg-red-50/60 dark:bg-red-500/5")}
                                            data-testid={`finance-installment-row-${inst.number}`}
                                        >
                                            <TableCell className={cn("font-mono text-sm", overdue && "border-l-2 border-red-500")}>
                                                {inst.number}
                                            </TableCell>
                                            <TableCell>
                                                <div>{formatDate(inst.due_date)}</div>
                                                {daysLate > 0 && (
                                                    <div className="text-xs text-red-600">
                                                        {daysLate} dia(s) em atraso
                                                    </div>
                                                )}
                                            </TableCell>
                                            <TableCell className="text-right font-medium">
                                                {formatBRL(inst.amount)}
                                            </TableCell>
                                            <TableCell className="text-right">
                                                {formatBRL(inst.paid_amount || 0)}
                                            </TableCell>
                                            <TableCell className="text-right">
                                                {formatBRL(balance)}
                                            </TableCell>
                                            <TableCell>
                                                <FinanceStatusBadge status={inst.status} />
                                            </TableCell>
                                            <TableCell className="text-right">
                                                {canPay && (
                                                    <Button
                                                        size="sm"
                                                        variant="secondary"
                                                        onClick={() => setPayingInst(inst)}
                                                        data-testid={`register-payment-open-${inst.number}`}
                                                    >
                                                        Registrar pagamento
                                                    </Button>
                                                )}
                                            </TableCell>
                                        </TableRow>
                                    );
                                })}
                                {(!a.installments || a.installments.length === 0) && (
                                    <TableRow>
                                        <TableCell colSpan={7} className="text-center text-sm text-muted-foreground">
                                            Nenhuma parcela.
                                        </TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </CardContent>
                </Card>

                <div className="space-y-4">
                    <Card data-testid="finance-account-summary">
                        <CardHeader>
                            <CardTitle className="flex items-center gap-2">
                                <CircleDollarSign className="h-4 w-4" />
                                Resumo
                            </CardTitle>
                        </CardHeader>
                        <CardContent className="space-y-3 text-sm">
                            <div className="flex items-center justify-between">
                                <span className="text-muted-foreground">Status</span>
                                <FinanceStatusBadge status={a.status} />
                            </div>
                            <div className="flex items-center justify-between">
                                <span className="text-muted-foreground">Total</span>
                                <span className="font-medium">{formatBRL(a.total_amount)}</span>
                            </div>
                            <div className="flex items-center justify-between">
                                <span className="text-muted-foreground">Pago</span>
                                <span className="text-emerald-600">{formatBRL(a.paid_amount || 0)}</span>
                            </div>
                            <div className="flex items-center justify-between">
                                <span className="text-muted-foreground">Saldo</span>
                                <span className="font-semibold">{formatBRL(a.balance || 0)}</span>
                            </div>
                            <Separator />
                            <div className="flex items-center justify-between">
                                <span className="text-muted-foreground">Categoria</span>
                                <span>{CATEGORY_LABELS[a.category] || a.category}</span>
                            </div>
                            <div className="flex items-center justify-between">
                                <span className="text-muted-foreground">Criada em</span>
                                <span>{formatDate(a.created_at)}</span>
                            </div>
                        </CardContent>
                    </Card>

                    <Card data-testid="finance-account-counterparty">
                        <CardHeader>
                            <CardTitle className="flex items-center gap-2">
                                {isReceivable ? <User className="h-4 w-4" /> : <Building2 className="h-4 w-4" />}
                                {isReceivable ? "Cliente" : "Fornecedor"}
                            </CardTitle>
                        </CardHeader>
                        <CardContent className="text-sm">
                            <div className="font-medium">
                                {a.counterparty_name || a.counterparty?.name || "—"}
                            </div>
                            {a.counterparty?.tax_id && (
                                <div className="text-xs text-muted-foreground">
                                    Documento: {a.counterparty.tax_id}
                                </div>
                            )}
                            {a.counterparty?.email && (
                                <div className="text-xs text-muted-foreground">
                                    Email: {a.counterparty.email}
                                </div>
                            )}
                            {a.counterparty?.phone && (
                                <div className="text-xs text-muted-foreground">
                                    Fone: {a.counterparty.phone}
                                </div>
                            )}
                        </CardContent>
                    </Card>

                    {(a.proposal_id || a.work_order_id) && (
                        <Card data-testid="finance-account-references">
                            <CardHeader>
                                <CardTitle className="flex items-center gap-2">
                                    <FileText className="h-4 w-4" />
                                    Referências
                                </CardTitle>
                            </CardHeader>
                            <CardContent className="space-y-2 text-sm">
                                {a.proposal_id && (
                                    <Link
                                        to={`/proposals/${a.proposal_id}`}
                                        className="flex items-center gap-2 text-primary hover:underline"
                                        data-testid="finance-goto-proposal"
                                    >
                                        <ExternalLink className="h-3.5 w-3.5" />
                                        Proposta {a.proposal_number || ""}
                                    </Link>
                                )}
                                {a.work_order_id && (
                                    <Link
                                        to={`/work-orders/${a.work_order_id}`}
                                        className="flex items-center gap-2 text-primary hover:underline"
                                        data-testid="finance-goto-wo"
                                    >
                                        <ExternalLink className="h-3.5 w-3.5" />
                                        Ordem de serviço {a.work_order_number || ""}
                                    </Link>
                                )}
                            </CardContent>
                        </Card>
                    )}
                </div>
            </div>

            <Card data-testid="finance-account-payments-history">
                <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                        <Receipt className="h-4 w-4" />
                        Histórico de pagamentos
                    </CardTitle>
                </CardHeader>
                <CardContent>
                    {(a.payments || []).length === 0 ? (
                        <p className="text-sm text-muted-foreground">Nenhum pagamento registrado ainda.</p>
                    ) : (
                        <Table>
                            <TableHeader>
                                <TableRow>
                                    <TableHead>Data</TableHead>
                                    <TableHead>Parcela</TableHead>
                                    <TableHead>Método</TableHead>
                                    <TableHead>Observação</TableHead>
                                    <TableHead className="text-right">Valor</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {(a.payments || []).map((p) => (
                                    <TableRow key={p.id} data-testid="finance-payment-row">
                                        <TableCell>{formatDate(p.paid_at)}</TableCell>
                                        <TableCell>#{p.installment_number}</TableCell>
                                        <TableCell>
                                            <Badge variant="outline">
                                                {PAYMENT_METHOD_LABELS[p.method] || p.method}
                                            </Badge>
                                        </TableCell>
                                        <TableCell className="text-sm text-muted-foreground">
                                            {p.notes || "—"}
                                        </TableCell>
                                        <TableCell className="text-right font-medium">
                                            {formatBRL(p.amount)}
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    )}
                </CardContent>
            </Card>

            <RegisterPaymentDialog
                open={!!payingInst}
                onOpenChange={(v) => !v && setPayingInst(null)}
                installment={payingInst}
                accountLabel={a.number}
                onSubmit={(payload) => payMut.mutateAsync(payload)}
                submitting={payMut.isPending}
            />

            <AlertDialog open={cancelOpen} onOpenChange={setCancelOpen}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>Cancelar conta {a.number}?</AlertDialogTitle>
                        <AlertDialogDescription>
                            Esta ação cancela todas as parcelas em aberto. Não é possível
                            cancelar contas que já receberam pagamentos.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel data-testid="finance-cancel-account-abort">
                            Manter
                        </AlertDialogCancel>
                        <AlertDialogAction
                            onClick={() => cancelMut.mutate()}
                            className="bg-red-600 hover:bg-red-700 text-white"
                            data-testid="finance-cancel-account-confirm"
                        >
                            Cancelar conta
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </div>
    );
}
